import { useEffect, useRef } from 'react';

const FOCUSABLE = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])';

/**
 * Piège à focus d'un panneau modal, avec restitution du focus à la fermeture.
 *
 * Pièges n°13 et n°14 : la maquette partageait une unique variable
 * `lastFocusedEl` entre le menu et la modale — le focus revenait au mauvais
 * élément quand les deux avaient été ouverts — et son `Escape` fermait les deux
 * panneaux même fermés, volant le focus au passage. Ici chaque panneau a son
 * propre mémorisateur, et `Escape` n'est écouté que pendant l'ouverture.
 */
const useFocusTrap = (isOpen, containerRef, onClose) => {
  const lastFocused = useRef(null);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;

    lastFocused.current = document.activeElement;
    const node = containerRef.current;

    const focusables = () =>
      Array.from(node ? node.querySelectorAll(FOCUSABLE) : []).filter((element) => element.offsetParent !== null);

    const first = focusables()[0];
    if (first) first.focus();

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const elements = focusables();
      if (!elements.length) return;
      const firstEl = elements[0];
      const lastEl = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const target = lastFocused.current;
      if (target && typeof target.focus === 'function') target.focus();
    };
  }, [isOpen, containerRef, onClose]);
};

export default useFocusTrap;
